import { Forwarding } from './notifications-forwardings.model';

/**
 * True when the list (in display order) is not strictly increasing by `order` —
 * e.g. two forwardings share an order value, so swapping them would change nothing.
 * Mirrors old CMT `ForwardingService.brokenOrderFieldInForwardingList`.
 */
export function hasBrokenOrder(list: readonly Forwarding[]): boolean {
  for (let i = 1; i < list.length; i++) {
    if (list[i - 1].order >= list[i].order) return true;
  }
  return false;
}

/**
 * Plans the order change that swaps `a` and `b` in `list` (the full section list in
 * display order). Returns the forwardings to persist, with their new `order`:
 * - intact sequence → only `a` and `b`, with their order values exchanged;
 * - broken sequence → every item, renumbered `1-n … 0` in display order and then
 *   swapped (old CMT `repairOrderIdsAndSwitchItemOrderId`), so the swap is effective.
 *
 * Does not mutate its input.
 */
export function planOrderSwap(list: readonly Forwarding[], a: Forwarding, b: Forwarding): Forwarding[] {
  const i = list.findIndex(f => f.forwardingId === a.forwardingId);
  const j = list.findIndex(f => f.forwardingId === b.forwardingId);
  if (i < 0 || j < 0 || i === j) return [];

  if (!hasBrokenOrder(list)) {
    return [
      { ...list[i], order: list[j].order },
      { ...list[j], order: list[i].order },
    ];
  }

  const repaired = list.map((f, index) => ({ ...f, order: index + 1 - list.length }));
  const orderI = repaired[i].order;
  repaired[i].order = repaired[j].order;
  repaired[j].order = orderI;
  return repaired;
}
